import React from 'react';
import { Box, Typography, Paper, Grid, Chip, Divider } from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VerifiedIcon from '@mui/icons-material/Verified';
import WorkIcon from '@mui/icons-material/Work';

const ContactPage = () => {
  const sectionStyle = { p: 3, height: '100%', borderRadius: '8px', border: '1px solid #e0e0e0' };

  return (
    <Box sx={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
      <Paper sx={{ p: 4, mb: 3, background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 100%)', borderRadius: '12px', color: 'white' }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          👨‍💼 Younes M.
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
          Expert — Credit Risk Modelling, Validation, Audit & Inspection
          with strong expertise in IT tools Python, R, SAS, React, and SQL
        </Typography>
      </Paper>

      <Grid container spacing={3}>
        {/* Contact Info */}
        <Grid item xs={12} md={5}>
          <Paper sx={sectionStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
              📬 Contact
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <EmailIcon sx={{ color: '#3498db' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">Email</Typography>
                  <Typography variant="body2" fontWeight={500}>younes.mouatassim@gmail.com</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PhoneIcon sx={{ color: '#2ecc71' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">Phone</Typography>
                  <Typography variant="body2" fontWeight={500}>06 08 50 57 39</Typography>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <LocationOnIcon sx={{ color: '#e74c3c' }} />
                <Box>
                  <Typography variant="caption" color="textSecondary">Location</Typography>
                  <Typography variant="body2" fontWeight={500}>Available worldwide — Remote & On-site</Typography>
                </Box>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Expertise */}
        <Grid item xs={12} md={7}>
          <Paper sx={sectionStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
              🎯 Areas of Expertise
            </Typography>
            <Grid container spacing={1.5}>
              {[
                { title: 'Credit Risk Modelling', desc: 'LGD, CCF, EAD model development under CRR II and CRR3, IFRS9 frameworks and Basel III/IV frameworks, Climate Stress Testing, Merton framework' },
                { title: 'Model Validation', desc: 'Independent quantitative and qualitative validation. Backtesting, benchmarking, sensitivity analysis, and documentation review.' },
                { title: 'Audit & Inspection', desc: 'ECB, EBA, and national regulator inspection readiness. Gap analysis, remediation plans, and regulatory compliance.' },
                { title: 'Regulatory Frameworks', desc: 'CRR2, CRR3, IFRS9 , Basel III/IV capital requirements, EBA Guidelines on PD/LGD estimation, LGD downturn requirements, Guidelines on climate risk' },
              ].map((exp, i) => (
                <Grid item xs={12} sm={6} key={i}>
                  <Box sx={{ p: 1.5, background: '#f9f9f9', borderRadius: '6px', height: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                      <VerifiedIcon sx={{ color: '#1abc9c', fontSize: '16px' }} />
                      <Typography variant="body2" fontWeight={600}>{exp.title}</Typography>
                    </Box>
                    <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1.5 }}>{exp.desc}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>

        {/* Skills & Technologies */}
        <Grid item xs={12}>
          <Paper sx={sectionStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
              ⚙️ Skills & Technologies
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['Python', 'R', 'SAS', 'SQL', 'Excel/VBA', 'React', 'CRR II','CRR3', 'IFRS 9', 'Basel III/IV', 'EBA Guidelines', 
                'LGD Modelling', 'CCF/EaD modelling ', 'PD Modelling','scoring methods', 'Economic modelling & projection', ' Analysis',
                'Project management', 'VaR', 'Default correclation', 'vasieck-Merton framework', 'Backtesting', 'Stress Testing', 'Documentation',
                'Model Governance', 'ECB Inspection', 'Data Quality', 'Risk Analytics'].map(skill => (
                <Chip key={skill} label={skill} size="small" sx={{ background: '#ecf0f1', fontSize: '11px' }} />
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Experience Summary */}
        <Grid item xs={12}>
          <Paper sx={sectionStyle}>
            <Typography variant="h6" fontWeight={600} sx={{ mb: 2, color: '#2c3e50' }}>
              💼 Experience Summary
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {[
                { role: ' Credit Risk Consultant', period: 'Present', desc: 'End-to-end PD, LGD and CCF model development, validation, and regulatory audit for European banks: Regulatory and IFRS9' },
                { role: 'Model Validation Specialist', period: 'Prior', desc: 'Independent review of IRB and IFRS 9 models. ECB inspection preparation and remediation.' },
                { role: 'Senior Risk Modeller', period: 'Prior', desc: 'Development of PD, LGD, CCF models for retail and corporate portfolios. SAS/Python implementation.' },
              ].map((exp, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 2 }}>
                  <WorkIcon sx={{ color: '#1abc9c', mt: 0.3 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>{exp.role}</Typography>
                    <Typography variant="caption" color="textSecondary">{exp.desc}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ContactPage;
